/**
 * The "Remind me" pill on an event card (2026-09-18 owner ask). Hidden once
 * the event has started (nothing to remind of); "Reminder set" after a
 * successful tap on this device. The flow and the copy live in
 * lib/eventReminders, the same reminder the transfer bonus card sets.
 */
import { useEffect, useState, type CSSProperties } from "react";
import { useLocation } from "wouter";
import { Bell, Check, Radio } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { getPlatform } from "@/lib/platform";
import type { GameEvent } from "@/lib/data";
import { useAttendingIds } from "@/lib/eventAttending";
import { coversEvent, useLiveEventPrefs } from "@/lib/liveEventPrefs";
import {
  EVENT_REMINDERS_EVENT,
  eventReminderAt,
  hasEventReminder,
  setEventReminder,
  type RemindableEvent,
} from "@/lib/eventReminders";

export function EventRemindButton({
  event,
  testId,
  className,
  style,
}: {
  event: RemindableEvent;
  testId: string;
  className?: string;
  style?: CSSProperties;
}) {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [busy, setBusy] = useState(false);
  const [isSet, setIsSet] = useState(() => hasEventReminder(event.id));
  useEffect(() => {
    const refresh = () => setIsSet(hasEventReminder(event.id));
    refresh();
    window.addEventListener(EVENT_REMINDERS_EVENT, refresh);
    return () => window.removeEventListener(EVENT_REMINDERS_EVENT, refresh);
  }, [event.id]);
  // The standing choice (lib/liveEventPrefs, 2026-10-30): an event whose type
  // the trainer ticked needs no tap. The button becomes a quiet "Countdown
  // set" chip that opens the Live events settings.
  const prefs = useLiveEventPrefs();
  const attending = useAttendingIds();
  const covered =
    event.category != null &&
    coversEvent(
      prefs,
      { category: event.category as GameEvent["category"], geoLocked: event.geoLocked, start: event.start, end: event.end },
      attending.includes(event.id),
    );
  // Nothing to remind of once it has started.
  if (!eventReminderAt(event.start)) return null;

  if (covered && prefs) {
    const card = prefs.delivery === "live";
    const label = card ? "Countdown set" : "Notification set";
    return (
      <button
        type="button"
        onClick={() => setLocation("/settings")}
        data-testid={testId}
        data-state="covered"
        aria-label={`${label} for ${event.title} from your Live events settings. Opens Settings.`}
        title="Set from your Live events settings"
        className={className ?? "inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-semibold hover-elevate"}
        style={
          style ?? { background: "hsl(140 70% 50% / 0.18)", border: "1px solid hsl(140 70% 50% / 0.5)", color: "hsl(140 70% 60%)" }
        }
      >
        {card ? <Radio className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />} {label}
      </button>
    );
  }

  async function onTap() {
    if (busy) return;
    setBusy(true);
    try {
      const outcome = await setEventReminder(event);
      switch (outcome.kind) {
        case "set":
          toast({
            title: "Reminder set.",
            description: `You will get a notification when it starts (${outcome.when}, your time). It is also in Your plan on the Calendar.`,
          });
          break;
        case "set-live": {
          const android = getPlatform() === "android";
          toast({
            title: "Countdown set.",
            description:
              outcome.mode === "local"
                ? android
                  ? "It starts within the hour: the countdown is in your notifications now. It is also in Your plan on the Calendar."
                  : "It starts within the hour: the countdown is on your Lock Screen now. It is also in Your plan on the Calendar."
                : android
                  ? `An hour before it starts (${outcome.when}, your time) a countdown appears in your notifications and on your lock screen. It is also in Your plan on the Calendar.`
                  : `An hour before it starts (${outcome.when}, your time) a countdown appears on your Lock Screen and in the Dynamic Island. It is also in Your plan on the Calendar.`,
          });
          break;
        }
        case "plan-only":
          toast({
            title: outcome.channel === "in-app" ? "Added to Your plan." : "Event reminders are off in Settings.",
            description: "It is in Your plan on the Calendar. Choose Push for Event reminders in Settings to get a notification too.",
          });
          break;
        case "denied":
          toast({
            title: "Notifications are off for Nexus Dex.",
            description: "Turn them on for Nexus Dex in your phone's Settings, then tap Remind me again. It is in Your plan on the Calendar.",
          });
          break;
        case "failed":
          toast({
            title: "Could not set the notification.",
            description: `${outcome.detail} It is in Your plan on the Calendar.`,
          });
          break;
        case "past":
          toast({ title: "That event has already started." });
          break;
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      onClick={onTap}
      disabled={busy}
      data-testid={testId}
      data-state={isSet ? "set" : "unset"}
      aria-label={isSet ? `Reminder set for ${event.title}` : `Remind me when ${event.title} starts`}
      className={className ?? "inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-semibold hover-elevate"}
      style={
        style ??
        (isSet
          ? { background: "hsl(140 70% 50% / 0.18)", border: "1px solid hsl(140 70% 50% / 0.5)", color: "hsl(140 70% 60%)" }
          : { background: "var(--surface-sunken)", border: "1px solid var(--border-subtle)" })
      }
    >
      {isSet ? <><Check className="w-3.5 h-3.5" /> Reminder set</> : <><Bell className="w-3.5 h-3.5" /> Remind me</>}
    </button>
  );
}
